/** Appointment calendar with day and week views, used on the main Appointments page. */

import interactionPlugin from '@fullcalendar/interaction'
import luxonPlugin from '@fullcalendar/luxon3'
import FullCalendar from '@fullcalendar/react'
import resourcePlugin from '@fullcalendar/resource'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import scrollGridPlugin from '@fullcalendar/scrollgrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import { useEffect, useMemo, useState } from 'react'

import {
  APPOINTMENT_TYPES,
  APPOINTMENT_TYPES_BY_ID,
  type AppointmentWithRelations,
  type DateFormat,
  getClinicRegion,
  getClinicTimezone,
  toWallClockDate,
  toWallClockTime,
} from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import { Calendar } from '@/frontend/components/ui/calendar'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/frontend/components/ui/popover'
import {
  AppointmentForm,
  appointmentToFormValues,
} from '@/frontend/features/appointments/appointment.form'
import {
  CALENDAR_VIEWS,
  formatCalendarTitle,
  getDefaultScrollTime,
  renderEventContent,
  SLOT_END,
  SLOT_START,
  toCalendarEvent,
  toLocalDateString,
} from '@/frontend/features/appointments/appointment-calendar.helpers'
import {
  type CalendarInteractionState,
  useAppointmentCalendarContext,
} from '@/frontend/features/appointments/appointment-calendar-provider'
import { AppointmentDetail } from '@/frontend/features/appointments/appointment-detail'
import { AppointmentHoverCard } from '@/frontend/features/appointments/appointment-hover-card'
import { BlockedSlotForm } from '@/frontend/features/appointments/blocked-slot.form'
import { BlockedSlotDetail } from '@/frontend/features/appointments/blocked-slot-detail'
import { BlockedSlotHoverCard } from '@/frontend/features/appointments/blocked-slot-hover-card'
import { useAppointmentCalendarData } from '@/frontend/features/appointments/use-appointment-calendar-data'

import '@/frontend/styles/fullcalendar.css'

/** Slot durations in ascending order (zoomed-in → zoomed-out). */
const SLOT_DURATIONS = ['00:05:00', '00:10:00', '00:15:00', '00:30:00', '01:00:00'] as const
const DEFAULT_DURATION_IDX = 2 // 00:15:00
const ZOOM_STORAGE_KEY = 'appointments-zoom'

/** Restores the saved slot duration, ignoring values no longer offered. */
function loadDurationIdx() {
  try {
    const saved = localStorage.getItem(ZOOM_STORAGE_KEY)
    const idx = SLOT_DURATIONS.findIndex((duration) => duration === saved)
    return idx === -1 ? DEFAULT_DURATION_IDX : idx
  } catch {
    return DEFAULT_DURATION_IDX
  }
}

export function AppointmentCalendar() {
  const {
    state,
    calendar,
    availability,
    modal,
    hoveredEvent,
    pendingChanges,
    eventHandlers,
    dispatch,
    requestAvailability,
    onAppointmentSaved,
    discardPendingChanges,
  } = useAppointmentCalendarContext()
  const { staffIds: staffIdsFilter, statuses } = state
  const { calendarRef, navigateTo, handleDatesSet } = calendar
  const { initialPosition, initialClinicOptions, calendarPosition, dateRange } = calendar

  const { clinic: clinicId, view: viewType } = calendarPosition
  const timeZone = getClinicTimezone(clinicId)
  // 12-hour (AM/PM) vs 24-hour time-axis labels follow the clinic's region convention (US 12h, UK 24h).
  const { hour12, locale } = getClinicRegion(clinicId)
  // The frame the grid itself is drawn in. Hover cards anchored to a slot read in
  // this rather than the viewer's, so their times match the axis beside them.
  const gridFmt: DateFormat = { timeZone, locale, hour12 }
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [durationIdx, setDurationIdx] = useState(loadDurationIdx)

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_STORAGE_KEY, SLOT_DURATIONS[durationIdx])
    } catch {
      // Keep the zoom in memory when browser storage is unavailable.
    }
  }, [durationIdx])

  const {
    calendarEvents,
    resources,
    error,
    isFetching,
    slotsLoading,
    slotsError,
    slotsEmpty,
    refetchSlots,
  } = useAppointmentCalendarData({
    clinicId,
    viewType,
    dateRange,
    staffIdsFilter,
    statuses,
    availability,
  })

  const displayedEvents = useMemo(
    () =>
      calendarEvents.map((event) => {
        if (!('extendedProps' in event) || !('appointment' in event.extendedProps)) return event
        const appointment = event.extendedProps.appointment
        const pending = pendingChanges[appointment.id]
        return pending ? toCalendarEvent({ ...appointment, ...pending }) : event
      }),
    [calendarEvents, pendingChanges]
  )

  const selectedId = modal?.type === 'view' ? modal.appointment.id : null
  const reschedulingId = availability?.type === 'rescheduling' ? availability.appointment.id : null

  const formattedTitle = useMemo(
    () => (dateRange ? formatCalendarTitle(dateRange.from, dateRange.to, viewType, timeZone) : ''),
    [dateRange, viewType, timeZone]
  )

  return (
    <>
      <div className="relative mt-3 flex-1 min-h-0">
        {availability && (slotsLoading || slotsError || slotsEmpty) && (
          <div className="pointer-events-none absolute inset-x-0 top-14 z-20 flex justify-center px-3">
            <div
              role="status"
              className="pointer-events-auto flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-md"
            >
              {slotsLoading ? (
                'Loading available slots…'
              ) : slotsError ? (
                <>
                  <span>Couldn&apos;t load available slots.</span>
                  <Button variant="link" onClick={() => refetchSlots()}>
                    Try again
                  </Button>
                </>
              ) : (
                'No available slots in this range. Try navigating forward.'
              )}
            </div>
          </div>
        )}
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger
            render={
              <button
                type="button"
                className="absolute left-1/2 top-0 z-20 flex h-9 -translate-x-1/2 cursor-pointer flex-col items-center justify-center rounded-[0.25rem] border-0 bg-transparent px-1 py-0 text-base font-semibold text-foreground hover:text-muted-foreground"
              />
            }
          >
            <span>{formattedTitle}</span>
            <span className="mt-0.5 text-2xs font-normal leading-tight text-muted-foreground">
              Times shown in {timeZone}
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              defaultMonth={dateRange?.from}
              selected={dateRange?.from}
              onSelect={(date) => {
                if (date) {
                  navigateTo({ date: toLocalDateString(date) })
                  setDatePickerOpen(false)
                }
              }}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        {isFetching && (
          <div className="absolute inset-0 top-[3rem] z-10 flex items-center justify-center pointer-events-none">
            <div className="size-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 top-[3rem] z-10 flex items-center justify-center bg-background/50">
            <p className="text-destructive-fg">Error loading appointments: {error.message}</p>
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          schedulerLicenseKey={import.meta.env.VITE_FC_LICENSE_KEY}
          plugins={[
            timeGridPlugin,
            resourcePlugin,
            resourceTimeGridPlugin,
            scrollGridPlugin,
            interactionPlugin,
            luxonPlugin,
          ]}
          initialView={CALENDAR_VIEWS[initialPosition.view]}
          initialDate={initialPosition.date}
          headerToolbar={{
            left: 'prev,next today',
            center: '',
            right: 'zoomOut,zoomIn resourceTimeGridDay,timeGridWeek',
          }}
          customButtons={{
            today: {
              text: 'Today',
              click: () =>
                navigateTo({
                  date: toWallClockDate(new Date(), timeZone),
                  time: getDefaultScrollTime(timeZone),
                }),
            },
            [CALENDAR_VIEWS.day]: {
              text: 'Day',
              click: () => navigateTo({ view: 'day' }),
            },
            [CALENDAR_VIEWS.week]: {
              text: 'Week',
              click: () => navigateTo({ view: 'week' }),
            },
            zoomIn: {
              text: 'Zoom in',
              click: () => setDurationIdx((i) => Math.max(0, i - 1)),
            },
            zoomOut: {
              text: 'Zoom out',
              click: () => setDurationIdx((i) => Math.min(SLOT_DURATIONS.length - 1, i + 1)),
            },
          }}
          resources={resources}
          firstDay={1}
          {...initialClinicOptions}
          slotMinTime={SLOT_START}
          slotMaxTime={SLOT_END}
          slotDuration={SLOT_DURATIONS[durationIdx]}
          scrollTimeReset={false}
          snapDuration="00:05:00"
          editable={!availability}
          eventDurationEditable={false}
          views={{ resourceTimeGridDay: { dayMinWidth: 155 } }}
          // biome-ignore lint/suspicious/noExplicitAny: This is hell to type otherwise
          eventOrder={(a: any, b: any) => {
            const aCancelled = a.extendedProps.appointment?.status === 'cancelled' ? 0 : 1
            const bCancelled = b.extendedProps.appointment?.status === 'cancelled' ? 0 : 1
            if (aCancelled !== bCancelled) return aCancelled - bCancelled
            const aCreated = a.extendedProps.appointment?.createdAt ?? ''
            const bCreated = b.extendedProps.appointment?.createdAt ?? ''
            return aCreated < bCreated ? -1 : aCreated > bCreated ? 1 : 0
          }}
          eventDisplay="block"
          nowIndicator
          allDaySlot={false}
          height="100%"
          events={displayedEvents}
          eventContent={renderEventContent}
          eventClassNames={({ event }) => [
            ...(event.id === selectedId ? ['outline-3 outline-border outline-offset-1'] : []),
            ...(event.id === reschedulingId
              ? [
                  "after:content-[''] after:absolute after:inset-0 after:z-[3] after:rounded-[inherit] after:shadow-[inset_0_0_0_3px_var(--color-amber-400)] after:pointer-events-none after:animate-[calendar-rescheduling-pulse_2s_ease-in-out_infinite] motion-reduce:after:animate-none",
                ]
              : []),
          ]}
          datesSet={handleDatesSet}
          eventClick={eventHandlers.eventClick}
          eventDrop={eventHandlers.eventDrop}
          dateClick={eventHandlers.dateClick}
          eventMouseEnter={eventHandlers.eventMouseEnter}
          eventMouseLeave={eventHandlers.eventMouseLeave}
          eventDragStart={eventHandlers.eventDragStart}
        />
      </div>

      {modal?.type === 'view' && (
        <Dialog open onOpenChange={(open) => !open && dispatch({ type: 'DISMISS' })}>
          <DialogContent className="sm:max-w-2xl">
            <AppointmentDetail
              appointment={modal.appointment}
              fmt={gridFmt}
              onEdit={(appointment) => dispatch({ type: 'EDIT_APPOINTMENT', appointment })}
              onRescheduleOnCalendar={(appointment) =>
                requestAvailability({ type: 'rescheduling', appointment })
              }
              onClose={() => dispatch({ type: 'DISMISS' })}
            />
          </DialogContent>
        </Dialog>
      )}

      {modal?.type === 'edit' && (
        <EditAppointmentDialog
          modal={modal}
          timeZone={timeZone}
          onSuccess={onAppointmentSaved}
          onCancel={() => {
            discardPendingChanges()
            dispatch({ type: 'DISMISS' })
          }}
        />
      )}

      {modal?.type === 'create' && (
        <CreateAppointmentDialog
          modal={modal}
          timeZone={timeZone}
          onSuccess={onAppointmentSaved}
          onCancel={() => dispatch({ type: 'DISMISS' })}
        />
      )}

      {modal?.type === 'block-view' && (
        <Dialog open onOpenChange={(open) => !open && dispatch({ type: 'DISMISS' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Blocked time</DialogTitle>
            </DialogHeader>
            <BlockedSlotDetail
              block={modal.block}
              fmt={gridFmt}
              clinicId={clinicId}
              timeZone={timeZone}
              onClose={() => dispatch({ type: 'DISMISS' })}
            />
          </DialogContent>
        </Dialog>
      )}

      {modal?.type === 'block-create' && (
        <CreateBlockedSlotDialog
          modal={modal}
          timeZone={timeZone}
          onSuccess={() => dispatch({ type: 'DISMISS' })}
          onCancel={() => dispatch({ type: 'DISMISS' })}
        />
      )}

      {hoveredEvent?.type === 'appointment' && (
        <AppointmentHoverCard
          appointment={hoveredEvent.appointment}
          anchorEl={hoveredEvent.el}
          fmt={gridFmt}
        />
      )}

      {hoveredEvent?.type === 'block' && (
        <BlockedSlotHoverCard block={hoveredEvent.block} anchorEl={hoveredEvent.el} fmt={gridFmt} />
      )}
    </>
  )
}

function EditAppointmentDialog({
  modal,
  timeZone,
  onSuccess,
  onCancel,
}: {
  modal: Extract<CalendarInteractionState['modal'], { type: 'edit' }>
  timeZone: string
  onSuccess: (appointment: AppointmentWithRelations) => void
  onCancel: () => void
}) {
  const { appointment, params } = modal
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Appointment</DialogTitle>
        </DialogHeader>
        <AppointmentForm
          defaultValues={appointmentToFormValues(
            params ? { ...appointment, ...params } : appointment,
            timeZone
          )}
          timeZone={timeZone}
          appointmentId={appointment.id}
          patientDetails={appointment.patient}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </DialogContent>
    </Dialog>
  )
}

function CreateAppointmentDialog({
  modal,
  timeZone,
  onSuccess,
  onCancel,
}: {
  modal: Extract<CalendarInteractionState['modal'], { type: 'create' }>
  timeZone: string
  onSuccess: (appointment: AppointmentWithRelations) => void
  onCancel: () => void
}) {
  const {
    date,
    endDate: paramEndDate,
    staffId,
    clinicId,
    appointmentTypeId: paramTypeId,
  } = modal.params
  const appointmentType = paramTypeId ? APPOINTMENT_TYPES_BY_ID[paramTypeId] : APPOINTMENT_TYPES[0]
  const endDate = paramEndDate ?? new Date(date.getTime() + appointmentType.duration * 60_000)
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Appointment</DialogTitle>
        </DialogHeader>
        <AppointmentForm
          defaultValues={{
            appointmentTypeId: appointmentType.id,
            clinicId,
            staffId: staffId,
            date: toWallClockDate(date, timeZone),
            startTime: toWallClockTime(date, timeZone),
            endTime: toWallClockTime(endDate, timeZone),
          }}
          timeZone={timeZone}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </DialogContent>
    </Dialog>
  )
}

function CreateBlockedSlotDialog({
  modal,
  timeZone,
  onSuccess,
  onCancel,
}: {
  modal: Extract<CalendarInteractionState['modal'], { type: 'block-create' }>
  timeZone: string
  onSuccess: () => void
  onCancel: () => void
}) {
  const { date, staffId, clinicId } = modal.params
  const endDate = new Date(date.getTime() + 60 * 60 * 1000)
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Block time</DialogTitle>
        </DialogHeader>
        <BlockedSlotForm
          defaultValues={{
            staffId: staffId ?? '',
            clinicId,
            startDate: toWallClockDate(date, timeZone),
            startTime: toWallClockTime(date, timeZone),
            endDate: toWallClockDate(date, timeZone),
            endTime: toWallClockTime(endDate, timeZone),
            note: '',
          }}
          timeZone={timeZone}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </DialogContent>
    </Dialog>
  )
}
